/* PlanSectionAccordion — stepper-style accordion section used by the
 * planner's "Author the plan" tab. Replaces the long single-scroll form
 * with collapsible sections so the planner can focus on one chunk at a
 * time without losing the page-level overview.
 *
 * UX contract (iter97.43, per user-direction option (c)):
 *   - Header shows: 2-digit number, completion icon, label, and a 1-line
 *     SUMMARY of the section's values when collapsed.
 *   - Body slides open on click. Multiple sections can be open at once.
 *   - "Next →" button at the bottom auto-advances:
 *       · current is complete → collapse current + open next
 *       · current incomplete  → keep current open, open next, soft toast
 *   - The component is presentational; openness + completion are owned
 *     by the parent (PlanEditorInline / ManufacturingSpecPanel).
 *
 * It must work as a true page anchor: id={anchorId} is set on the root
 * so the right-side rail's scroll-into-view still works.
 */
import React from "react";
import { Button } from "@/components/ui/button";
import {
    CheckCircle, Warning, CaretDown, CaretRight, Circle,
} from "@phosphor-icons/react";

export default function PlanSectionAccordion({
    anchorId,
    number,
    label,
    summary,
    isOpen,
    isComplete,
    onToggle,
    onNext,
    nextLabel = "Next",
    hideNext = false,
    children,
    headerRight = null,
    testIdPrefix = "plan-section",
    /* iter98.99n — Accent rail. Each Author-the-plan section ties
       to the matching doctor-side wizard step (emerald = treatment
       plan output, rose = lab handoff). Colored 4px left-bar +
       tinted section number close the visual loop across the case
       lifecycle. */
    accent = null,
}) {
    /* iter98.99au — Per user direction (2026-02-19), the colored 4px
       left-bar treatment is dropped. The accent palette stays only for
       the section-number text color so the visual link to the doctor's
       wizard step is preserved without the heavy bar that was making
       the planner workspace feel busy. */
    const accentMap = {
        primary: { num: "text-primary",      borderOpen: "border-primary/30"    },
        violet:  { num: "text-violet-700",   borderOpen: "border-violet-300"    },
        amber:   { num: "text-amber-800",    borderOpen: "border-amber-300"     },
        emerald: { num: "text-emerald-700",  borderOpen: "border-emerald-300"   },
        rose:    { num: "text-rose-700",     borderOpen: "border-rose-300"      },
    };
    const a = accent ? (accentMap[accent] || null) : null;

    const completionIcon = isComplete
        ? <CheckCircle size={15} weight="fill" className="text-emerald-500" />
        : (summary && summary !== "Not authored")
            ? <Warning size={14} weight="fill" className="text-amber-500" />
            : <Circle size={11} weight="regular" className="text-border" />;

    return (
        <section
            id={anchorId}
            className={`scroll-mt-24 rounded-2xl border bg-card overflow-hidden transition-colors shadow-[0_1px_0_rgba(15,23,42,0.02),0_6px_18px_-14px_rgba(15,23,42,0.10)] ${
                isOpen
                    ? (a ? a.borderOpen : "border-teal-300/70") + " shadow-md"
                    : "border-border/70"
            }`}
            data-testid={`${testIdPrefix}-${anchorId}`}
            data-open={isOpen ? "true" : "false"}
            data-complete={isComplete ? "true" : "false"}
        >
            {/* iter101.56 — Header styling aligned with the Submission
                tab's polished accordions (Case Details / Clinical /
                Extractions / etc.). Teal editorial number, larger
                caps tracking, teal-tinted hover wash, teal caret.
                The per-section accent lives on `border-open` only so
                the visual link to the doctor's wizard step is
                preserved without breaking the unified typography. */}
            <button
                type="button"
                onClick={onToggle}
                className={`w-full flex items-center gap-3 px-5 py-3 border-border/60 hover:bg-teal-50/40 transition-colors text-left group ${isOpen ? "border-b" : ""}`}
                style={{ background: "linear-gradient(to right, rgba(20,184,166,0.05) 0%, transparent 55%)" }}
                aria-expanded={isOpen}
                aria-controls={`${anchorId}-body`}
                data-testid={`${testIdPrefix}-${anchorId}-header`}
            >
                <span className="font-display text-[13px] font-bold tabular-nums shrink-0 w-6 text-teal-700">
                    {String(number).padStart(2, "0")}
                </span>
                <span className="grid place-items-center w-5 h-5 shrink-0">{completionIcon}</span>
                <h3 className="text-[11px] font-bold tracking-[0.14em] uppercase text-teal-900/80 shrink-0">{label}</h3>
                {!isOpen && summary && (
                    <span
                        className={`ml-3 text-[11px] truncate min-w-0 normal-case tracking-normal ${isComplete ? "text-muted-foreground/80" : "text-muted-foreground/60 italic"}`}
                        data-testid={`${testIdPrefix}-${anchorId}-summary`}
                    >
                        {summary}
                    </span>
                )}
                <span className="ml-auto flex items-center gap-2 shrink-0">
                    {headerRight}
                    <CaretDown
                        size={13}
                        weight="bold"
                        className={`transition-transform text-teal-700 group-hover:scale-110 ${isOpen ? "rotate-180" : ""}`}
                    />
                </span>
            </button>
            {/* Body. We keep it mounted (no unmount on collapse) so any
                in-flight debounced saves complete; CSS handles the visual
                collapse. Hidden via display:none for accessibility + so
                hidden form inputs aren't focusable. */}
            <div
                id={`${anchorId}-body`}
                className={`border-t border-border/40 ${isOpen ? "block" : "hidden"}`}
                role="region"
                aria-labelledby={`${anchorId}-header`}
            >
                <div className="px-5 py-4">{children}</div>
                {!hideNext && onNext && isOpen && (
                    <div className="px-5 py-3 border-t border-border/30 bg-secondary/20 flex items-center justify-end gap-2">
                        <span className="text-[11px] text-muted-foreground mr-auto">
                            {isComplete ? "Looks good." : "You can come back to fill the rest later."}
                        </span>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={onNext}
                            className="rounded-full"
                            data-testid={`${testIdPrefix}-${anchorId}-next`}
                        >
                            {nextLabel} <CaretRight size={12} weight="bold" className="ml-1" />
                        </Button>
                    </div>
                )}
            </div>
        </section>
    );
}
